import {Auto, Teleop, Penalties, season, Randomization, validationErrors} from "./seasons/Decode";
import {t} from "ftc-common/i18n";
import {calculateScoreBreakdown, defaultScore, ScoreType} from "./seasons/Decode/scores";
import {useMatchTimer} from "ftc-common/hooks";
import {Alert, Button, OverlayTrigger, Popover} from "react-bootstrap";
import {ReactNode, useEffect, useId} from "react";
import {messageDialog} from "first-common/dialogs";

import './ScoreCalculator.css';
import {useLocalStorageState} from "first-common/utils";
import { Temporal } from "temporal-polyfill";

function formatTime(time: number) {
    return `${Math.floor(time / 60)}:${(time % 60).toString().padStart(2, '0')}`
}

export function ScoreCalculator() {
    // Bump this when bumping the season to effectively reset the calculator
    const [scoreData, updateScoreData] = useLocalStorageState<ScoreType>(`score-calculator-score-decode`, () => {
        return defaultScore
    });
    const [random, setRandom] = useLocalStorageState<number>(`score-calculator-random-decode`, 0);
    const [useTimer, setUseTimer] = useLocalStorageState<boolean>(`score-calculator-use-timer`, false);
    const [startTime, setStartTime] = useLocalStorageState<number>(`score-calculator-start-time`, 0);
    const [scoreCalculatorUsed, setScoreCalculatorUsed] = useLocalStorageState<boolean>(`score-calculator-used`, false);
    const id = useId();

    function setScoreValue<K extends keyof ScoreType>(key: K, value: ScoreType[K]) {
        updateScoreData((was : ScoreType) => {
            const newScores = {...was, [key]: value}
            calculateScoreBreakdown(newScores, random);

            return newScores;
        })
        setScoreCalculatorUsed((was) => {
            if(!was) {
                plausible('ScoreCalculatorUsed')
            }
            return true;
        })
    }

    const mute = false
    const timer = useMatchTimer({
        startAt: startTime,
        nowCallback: () => Date.now(),
        sounds: mute ? {} : (
            {
                [120 + 30 + 8]: 'matchStart',
                [120 + 8]: 'autoEnd',
                [120 + 6]: 'controllers',
                [120 + 3]: 'countdown',
                [120]: 'startTeleop',
                [20]: 'endgameWarning',
                [0]: 'matchEnd'
            })
    }, {
        auto: Temporal.Duration.from({ seconds: 30 }),
        transition: Temporal.Duration.from({ seconds: 8 }),
        teleop: Temporal.Duration.from({ seconds: 120 }),
        endgameWarning: Temporal.Duration.from({ seconds: 20 }),
        total: Temporal.Duration.from({ seconds: 30 + 8 + 120 }),
    })
    let displayTime: [ReactNode] = [t`Period Time: `]
    if(startTime != 0) {
        if (timer.auto) {
            displayTime.push(formatTime(timer.remaining - 120 - 8), ' ', <i className={`fas fa-robot`} />);
        } else if (timer.transition) {
            displayTime.push(formatTime(timer.remaining - 120), ' ', <i className={`fas fa-hand`} />);
        } else if (timer.teleop || timer.endgame) {
            displayTime.push(formatTime(timer.remaining), ' ', <i className={`fas fa-gamepad`} />);
        } else if (timer.done) {
            displayTime.push(formatTime(0));
        }
    } else {
        displayTime = [t`Timer not started`]
    }

    const scoreProps = {
        score: scoreData,
        setScoreValue
    }

    useEffect(() => {
        if(import.meta.env.DEV) {
            return;
        }
        messageDialog({
            label: t`Warning`,
            text: t`This calculator is for team practice only.<br/>Do NOT use this at events.`,
            dangerous: true
        })
    }, [])

    const errors = validationErrors(scoreData);

    return <>
        <div className="row justify-content-center">
            <Alert variant={"warning"}>
                <i className="fa fa-exclamation-triangle"></i>&nbsp;
                {t`This calculator is for team practice only.  Do NOT use this at events.`}
                &nbsp;<i className="fa fa-exclamation-triangle"></i>
            </Alert>
            <div className="col-12 text-center">
                <p className={"h2"}>{t`${season} Score Calculator`}</p>
            </div>
        </div>
        <div className=" row justify-content-center scoreArea">
            <div className="col-12 col-sm-10 text-center">
                <p className={"h3"}>
                    {t`Total Score: ${scoreData.preFoulTotal}`}
                    {errors.length > 0 ?
                        <OverlayTrigger trigger={["click", "hover"]} placement={"bottom"} overlay={<Popover id={`validation-errors-${id}`}>
                            <Popover.Content>
                                <ul className={"mb-0"}>
                                    {errors.map((e, idx) => <li key={idx}>{e}</li>)}
                                </ul>
                            </Popover.Content>
                        </Popover>}>
                            <i className={"fa fa-exclamation-triangle"}></i>
                        </OverlayTrigger>
                    : null}
                </p>
            </div>
            <div className={"col-12 col-sm-10"}>
                <table className={"table table-bordered table-sm horizontalBreakout text-center"} style={{tableLayout: 'fixed'}}>
                    <thead>
                    <tr>
                        <th>{t`AUTO`}</th>
                        <th>{t`TELEOP`}</th>
                        <th>{t`FOUL`}</th>
                        <th colSpan={3}>{t`RP`}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td rowSpan={2}>{scoreData.autoPoints}</td>
                        <td rowSpan={2}>{scoreData.teleopPoints}</td>
                        <td rowSpan={2}>{scoreData.foulPointsCommitted}<br/>{t`(to other alliance)`}</td>
                        <th>MOVEMENT</th>
                        <th>GOAL</th>
                        <th>PATTERN</th>
                    </tr>
                    <tr>
                        <td><i className={`fa fa-${scoreData.movementRP ? 'check' : 'x'}`}></i> ({scoreData.movementRPProgress[0]}/{scoreData.movementRPProgress[1]})</td>
                        <td><i className={`fa fa-${scoreData.goalRP ? 'check' : 'x'}`}></i> ({scoreData.goalRPProgress[0]}/{scoreData.goalRPProgress[1]})</td>
                        <td><i className={`fa fa-${scoreData.patternRP ? 'check' : 'x'}`}></i> ({scoreData.patternRPProgress[0]}/{scoreData.patternRPProgress[1]})</td>
                    </tr>
                    </tbody>
                </table>
                <details className={"verticalBreakout"}>
                    <summary>Score details</summary>

                    <table className={"table table-bordered table-sm text-center"} style={{tableLayout: 'fixed'}}>
                        <tbody>
                        <tr>
                            <th>{t`AUTO`}</th>
                            <td colSpan={2}>{scoreData.autoPoints}</td>
                        </tr>
                        <tr>
                            <th>{t`TELEOP`}</th>
                            <td colSpan={2}>{scoreData.teleopPoints}</td>
                        </tr>
                        <tr>
                            <th>{t`FOUL`}</th>
                            <td colSpan={2}>{scoreData.foulPointsCommitted} {t`(to other alliance)`}</td>
                        </tr>
                        <tr>
                            <th colSpan={3}>{t`RP`}</th>
                        </tr>
                        <tr>
                            <th>MOVEMENT</th>
                            <th>GOAL</th>
                            <th>PATTERN</th>
                        </tr>
                        <tr>
                            <td><i className={`fa fa-${scoreData.movementRP ? 'check' : 'x'}`}></i> ({scoreData.movementRPProgress[0]}/{scoreData.movementRPProgress[1]})</td>
                            <td><i className={`fa fa-${scoreData.goalRP ? 'check' : 'x'}`}></i> ({scoreData.goalRPProgress[0]}/{scoreData.goalRPProgress[1]})</td>
                            <td><i className={`fa fa-${scoreData.patternRP ? 'check' : 'x'}`}></i> ({scoreData.patternRPProgress[0]}/{scoreData.patternRPProgress[1]})</td>
                        </tr>
                        </tbody>
                    </table>
                </details>
            </div>
        </div>
        <div className="row justify-content-center">
            <div className={"col-12 col-sm-10 text-center"}>
                <label htmlFor={"enable-timer"}>{t`Enable Timer:`}</label> <input id="enable-timer" type={"checkbox"} checked={useTimer} onChange={(e) => setUseTimer((e.target as HTMLInputElement).checked)} />
            </div>
        </div>
        {useTimer ?
            <div className="row justify-content-center">
                {timer.running ?
                    <Button variant={"danger"} onClick={() => setStartTime(0)} className={"col-11 col-sm-10"}>{t`Abort Match`}</Button> :
                    <Button variant={"success"} onClick={() => setStartTime(Date.now())} className={"col-11 col-sm-10"}>{t`Start Timer`}</Button> }

                <div className={"col-10 text-center"}>
                    {displayTime}
                </div>
            </div> : null}
        <div className="row justify-content-center mb-2">
            <Randomization random={random} setRandom={(val) => {
                setRandom(val)
                updateScoreData((was : ScoreType) => {
                    const newScores = {...was}
                    calculateScoreBreakdown(newScores, val);

                    return newScores;
                })
            }}/>
        </div>
        <div className="row justify-content-center">
            <div className="col-11 col-sm-10 alert alert-dark text-center">
                AUTO Scores
            </div>
        </div>
        <Auto {...scoreProps} random={random} />
        <div className="row justify-content-center">
            <div className="col-11 col-sm-10 alert alert-dark text-center">
                TELEOP Scores
            </div>
        </div>
        <Teleop {...scoreProps} random={random} />
        <Penalties {...scoreProps} />
        <div className="row justify-content-center">
            <Button variant={"warning"} onClick={() => {
                setStartTime(0)
                setScoreCalculatorUsed(false)
                setRandom(0)
                updateScoreData(defaultScore)
            }} className={"col-11 col-sm-10"}>{t`Reset`}</Button>
        </div>
        {/* Since penalties are subtractive there is no good way to show them here */}
    </>;
}